import { useEffect, useState } from "react";
import {
  getRecommendations,
  RecommendationProduct,
  RecommendationProps,
} from "@repo/apis/recommendations";
import { ChameleonRequest } from "@repo/chameleon-request";
import { useAuth } from "@repo/ui/auth";
import { removeBrandPrefix } from "@repo/utils/remove-brand-prefix";

export type UseRecommendationsProps = RecommendationProps & {
  chameleonRequestInstance: ChameleonRequest;
  type?: "ymal" | "pab";
  withBrandName?: boolean;
  fitIndicators?: readonly string[] | string[];
};

export type RecommendedState = "loading" | "success" | "error";

export const useRecommendations = ({
  store,
  country,
  lang,
  currency,
  productids,
  chameleonRequestInstance,
  type = "ymal",
  withBrandName = true,
  limit = 16,
  fitIndicators,
}: UseRecommendationsProps) => {
  const { authClient } = useAuth();
  const [recommendations, setRecommendations] = useState<
    RecommendationProduct[]
  >([]);
  const [recommendedState, setRecommendedState] =
    useState<RecommendedState>("loading");

  useEffect(() => {
    if (!authClient || !productids.length) {
      setRecommendedState("error");
      return;
    }

    const fetchRecommendations = async () => {
      try {
        const authHeaderValue = await authClient.getAuthHeaderPayload();
        if (!authHeaderValue) {
          console.warn("No Auth Token");
          setRecommendedState("error");
          return;
        }

        const data = await getRecommendations({
          store,
          country,
          lang,
          currency,
          productids,
          chameleonRequestInstance,
          authHeaderValue,
          type,
          limit,
          shop: "topshop",
        });

        const processedRecommendations = data.recommendations.map(
          (product) => ({
            ...product,
            name: withBrandName
              ? product.name
              : removeBrandPrefix({
                  brand: product.brandName,
                  text: product.name,
                  fitIndicators,
                }),
          }),
        );

        setRecommendations(processedRecommendations);
        setRecommendedState("success");
      } catch (error) {
        console.error("Error fetching recommendations:", error);
        setRecommendedState("error");
      }
    };

    fetchRecommendations();
  }, [
    store,
    country,
    lang,
    currency,
    productids,
    chameleonRequestInstance,
    authClient,
    type,
    withBrandName,
    limit,
    fitIndicators,
  ]);

  return { recommendations, recommendedState };
};
